/**
 * Card for displaying upcoming, paid, and past-due invoices.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { uiState } from 'react-redux-ui-state';
import { withStyles } from '@material-ui/styles';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableFooter from '@material-ui/core/TableFooter';
import TablePagination from '@material-ui/core/TablePagination';

import ORTypography from 'components/ORTypography/ORTypography';
import TablePaginationActions from 'components/ORTable/TablePaginationActions';
import InvoiceItem from './InvoiceItem/InvoiceItem';

const propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape({}).isRequired,
  org: PropTypes.shape({
    upcomingInvoices: PropTypes.arrayOf(PropTypes.shape({})),
    pastInvoices: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  page: PropTypes.number.isRequired,
  isFastlaneCustomer: PropTypes.bool.isRequired,
  setUiState: PropTypes.func.isRequired,
};

const defaultProps = {
  className: null,
};

export function UnstyledInvoiceCard({
  className,
  classes,
  org,
  page,
  isFastlaneCustomer,
  setUiState,
}) {
  // Invoicing doesn't currently work for Fastlane customers
  if (isFastlaneCustomer) {
    return null;
  }

  const rowsPerPage = 6;

  function handleChangePage(event, newPage) {
    setUiState({
      page: newPage,
    });
  }

  const invoices = [...org.upcomingInvoices, ...org.pastInvoices];

  const invoiceRows = invoices
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    .map((invoice) => (
      <InvoiceItem
        key={invoice.date}
        status={invoice.paidStatus}
        dates={invoice.date}
        total={invoice.amountDueCents}
      />
    ));

  const invoiceTable = (
    <Table>
      <TableBody>{invoiceRows}</TableBody>
      <TableFooter>
        <TablePagination
          classes={{
            root: classes.paginationRoot,
            spacer: classes.paginationSpacer,
            caption: classes.paginationCaption,
            input: classes.paginationInput,
            toolbar: classes.paginationToolbar,
          }}
          colSpan={3}
          count={invoices.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          ActionsComponent={TablePaginationActions}
        />
      </TableFooter>
    </Table>
  );

  const noInvoiceMsg = (
    <ORTypography component="p" align="center" className={classes.noInvoiceMsg}>
      You have no previous invoices. Enjoy your day!
    </ORTypography>
  );

  return (
    <Card className={className}>
      <CardHeader title="Invoices" />
      <CardContent>{invoices.length !== 0 ? invoiceTable : noInvoiceMsg}</CardContent>
    </Card>
  );
}

UnstyledInvoiceCard.propTypes = propTypes;
UnstyledInvoiceCard.defaultProps = defaultProps;

const styles = (theme) => ({
  noInvoiceMsg: {
    padding: theme.spacing(2),
    fontSize: '12px',
    fontStyle: 'italic',
    color: '#4A4A4A',
  },
  paginationRoot: {
    fontSize: '1rem',
    paddingRight: 'unset',
    border: 'none',
    '& span': {
      color: theme.customColors.lightGrey,
      marginRight: theme.spacing(0.25),
      marginLeft: theme.spacing(0.25),
      display: 'inline-block',
      width: '20px',
      textAlign: 'center',
    },
    '& span:hover': {
      cursor: 'pointer',
    },
    '& button': {
      border: 'none',
      fontSize: '1rem',
      color: theme.palette.secondary.main,
      display: 'inline',
      cursor: 'pointer',
    },
  },
  paginationSpacer: {
    flex: 'unset',
  },
  // uses !important to prevent media query overwrites
  paginationCaption: {
    display: 'none !important',
  },
  paginationInput: {
    display: 'none !important',
  },
  paginationToolbar: {
    height: '30px',
    minHeight: '30px',
    paddingTop: '8px',
    paddingLeft: '0px',
  },
  pageButtonStyles: {
    fontSize: '1rem',
  },
});

export const uiConfig = {
  name: 'invoiceCard',
  state: () => ({
    page: 0,
  }),
};

export default uiState(uiConfig)(withStyles(styles)(UnstyledInvoiceCard));
